/**
 * /en/practice/$area — English per-practice-area depth page.
 *
 * Hand-authored English content from src/data/practice/en-a.ts and en-b.ts.
 * Mirrors the Thai /practice/$area lot without machine translation and without
 * any AI call at build or request time. No fees, no outcome guarantees.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { EN_PRACTICE, type EnPracticeArea } from "@/data/practice/en";
import { AnswerFirst } from "@/components/AnswerFirst";
import { SmartImage } from "@/components/SmartImage";
import { FirmCta } from "@/components/firm/FirmCta";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  faqPageSchema,
  howToSchema,
} from "@/lib/schema";

import heroLitigation from "@/assets/firm-litigation-prep.jpg";
import heroCorporate from "@/assets/firm-corporate-consult.jpg";
import heroFamily from "@/assets/firm-family-estate.jpg";
import heroEstate from "@/assets/practice-estate.jpg";
import heroProperty from "@/assets/practice-property.jpg";
import heroLabour from "@/assets/practice-labour.jpg";
import heroTax from "@/assets/practice-tax.jpg";
import heroTeam from "@/assets/firm-hero-team.jpg";

const HERO: Record<string, string> = {
  litigation: heroLitigation,
  corporate: heroCorporate,
  family: heroFamily,
  estate: heroEstate,
  property: heroProperty,
  labour: heroLabour,
  tax: heroTax,
  ip: heroCorporate,
  "notary-international": heroTeam,
  pdpa: heroLabour,
};

type Loaded = { area: EnPracticeArea; path: string };

export const Route = createFileRoute("/en/practice/$area")({
  loader: ({ params }) => {
    const area = EN_PRACTICE[params.area];
    if (!area) throw notFound();
    return { area, path: `/practice/${params.area}` } satisfies Loaded;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Practice area not found" }, { name: "robots", content: "noindex" }] };
    }
    const { area, path } = loaderData;
    const url = absoluteUrl(`/en${path}`, null, "en");
    return {
      meta: [
        { title: area.seoTitle },
        { name: "description", content: area.seoDesc },
        { property: "og:title", content: area.seoTitle },
        { property: "og:description", content: area.seoDesc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...canonicalAndHreflang(path, null, "en").filter((l) => l.rel === "alternate"),
      ],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({
              url: `/en${path}`,
              title: area.seoTitle,
              description: area.seoDesc,
              speakable: true,
            }),
            breadcrumbListSchema([
              { name: "Home", url: absoluteUrl("/en", null, "en") },
              { name: "Practice areas", url: absoluteUrl("/en/practice", null, "en") },
              { name: area.name, url },
            ]),
            legalServiceSchema({
              name: area.name,
              description: area.tagline,
              url: `/en${path}`,
              areaServed: "TH",
              serviceType: area.name,
            }),
            howToSchema({
              name: `How we handle ${area.name} matters`,
              description: area.tagline,
              steps: area.steps.map((s, i) => ({ name: `Step ${i + 1}`, text: s })),
            }),
            faqPageSchema(area.faqs),
          ]),
        ),
      ],
    };
  },
  component: EnPracticeAreaPage,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <h1 className="text-2xl font-bold text-foreground">Practice area not found</h1>
      <p className="mt-4 text-muted-foreground">
        Return to the{" "}
        <Link to="/en/practice" className="text-primary underline">
          practice-area overview
        </Link>{" "}
        to choose a topic.
      </p>
    </main>
  ),
});

function EnPracticeAreaPage() {
  const { area } = Route.useLoaderData();
  const hero = HERO[area.slug] ?? heroTeam;
  const related = area.related
    .map((slug) => EN_PRACTICE[slug])
    .filter((a): a is EnPracticeArea => Boolean(a));

  return (
    <main>
      <header
        className="border-b border-border/60 text-[var(--primary-foreground)]"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-12 md:py-16">
          <nav aria-label="Breadcrumb" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/en" className="hover:underline">
              Home
            </Link>{" "}
            <span aria-hidden>/</span>{" "}
            <Link to="/en/practice" className="hover:underline">
              Practice areas
            </Link>{" "}
            <span aria-hidden>/</span> {area.name}
          </nav>
          <div className="mt-6 grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <h1 className="text-3xl font-bold leading-tight tracking-tight md:text-4xl">{area.name}</h1>
              <p className="mt-3 text-sm font-semibold text-[var(--gold)]">{area.tagline}</p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold">
                <Link
                  to="/en/contact"
                  className="inline-flex min-h-[44px] items-center rounded-md bg-[var(--gold)] px-5 text-[var(--primary)]"
                >
                  Talk to a lawyer
                </Link>
                <Link
                  to="/en/practice"
                  className="inline-flex min-h-[44px] items-center rounded-md border border-[var(--gold)]/60 px-5 text-[var(--gold)]"
                >
                  All practice areas
                </Link>
              </div>
            </div>
            <SmartImage
              src={hero}
              alt={area.imageAlt}
              width={1200}
              height={800}
              priority
              weight="hero"
              className="w-full rounded-xl border border-[var(--gold)]/30 object-cover"
            />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <AnswerFirst>{area.answer}</AnswerFirst>

        <section aria-labelledby="audience" className="mt-12">
          <h2 id="audience" className="text-xl font-bold text-foreground md:text-2xl">
            Who this page is for
          </h2>
          <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
            {area.audience.map((s) => (
              <li key={s} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="scope" className="mt-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 id="scope" className="text-xl font-bold text-foreground md:text-2xl">
              What the firm actually handles
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
              {area.scope.map((s) => (
                <li key={s} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-2xl">Matters clients bring us</h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-muted-foreground">
              {area.examples.map((s) => (
                <li key={s} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="docs" className="mt-12 rounded-xl border border-border bg-card p-6">
          <h2 id="docs" className="text-xl font-bold text-foreground md:text-2xl">
            Documents to bring to the first meeting
          </h2>
          <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
            {area.docs.map((s) => (
              <li key={s} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            If some documents are missing, send what you have. The lawyer will tell you which office issues the
            remaining ones and which documents need certified translation before they can be used.
          </p>
        </section>

        <section aria-labelledby="steps" className="mt-12">
          <h2 id="steps" className="text-xl font-bold text-foreground md:text-2xl">
            How the work proceeds
          </h2>
          <ol className="mt-4 space-y-3">
            {area.steps.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">
                  {i + 1}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="cautions" className="mt-12">
          <h2 id="cautions" className="text-xl font-bold text-foreground md:text-2xl">
            Pitfalls and common misunderstandings
          </h2>
          <div className="mt-4 space-y-3">
            {area.cautions.map((c) => (
              <div key={c.title} className="rounded-lg border-l-4 border-[var(--gold)] bg-muted/50 p-4">
                <p className="text-sm font-semibold text-foreground">{c.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="compare" className="mt-12">
          <h2 id="compare" className="text-xl font-bold text-foreground md:text-2xl">
            {area.compare.heading}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{area.compare.intro}</p>
          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead className="bg-muted/60">
                <tr>
                  <th scope="col" className="p-3 font-semibold text-foreground">
                    Point
                  </th>
                  {area.compare.columns.map((c) => (
                    <th key={c} scope="col" className="p-3 font-semibold text-foreground">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {area.compare.rows.map((r) => (
                  <tr key={r.label} className="border-t border-border align-top">
                    <th scope="row" className="p-3 font-medium text-foreground">
                      {r.label}
                    </th>
                    {r.cells.map((cell, i) => (
                      <td key={`${r.label}-${i}`} className="p-3 leading-relaxed text-muted-foreground">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{area.compare.note}</p>
        </section>

        <section aria-labelledby="handoff" className="mt-12 rounded-xl border border-[var(--gold)]/40 bg-muted/40 p-6">
          <h2 id="handoff" className="text-xl font-bold text-foreground md:text-2xl">
            Hand the whole matter to the firm
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{area.handoff}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-semibold">
            <Link to="/en/contact" className="text-primary hover:underline">
              Talk to a lawyer about this →
            </Link>
            <Link to="/en/coverage" className="text-primary hover:underline">
              Service areas across Thailand →
            </Link>
          </div>
        </section>

        <section aria-labelledby="faq" className="mt-12">
          <h2 id="faq" className="text-xl font-bold text-foreground md:text-2xl">
            {area.name}: frequently asked questions
          </h2>
          <dl className="mt-6 space-y-5">
            {area.faqs.map((f) => (
              <div key={f.question} className="rounded-xl border border-border bg-card p-5">
                <dt className="text-base font-semibold text-foreground">{f.question}</dt>
                <dd className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="related" className="mt-12">
          <h2 id="related" className="text-xl font-bold text-foreground md:text-2xl">
            Practice areas often handled together with this one
          </h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-3">
            {related.map((r) => (
              <li key={r.slug} className="rounded-xl border border-border bg-card p-4">
                <Link
                  to="/en/practice/$area"
                  params={{ area: r.slug }}
                  className="font-semibold text-foreground hover:underline"
                >
                  {r.name}
                </Link>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{r.tagline}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/en/practice" className="text-primary hover:underline">
              All practice areas →
            </Link>
            <Link to="/practice/$area" params={{ area: area.slug }} className="text-primary hover:underline">
              อ่านหน้านี้เป็นภาษาไทย →
            </Link>
            <Link to="/en/contact" className="text-primary hover:underline">
              Contact the firm →
            </Link>
          </div>
        </section>
      </div>

      <FirmCta heading={`Speak with the lawyer responsible for ${area.name}`} />
    </main>
  );
}
